import type { CSSProperties, ReactNode } from "react";
import { Button } from "./button";
import { cx } from "./cx";

/* -------------------------------------------------------------- Skeleton */

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span className={cx("ui-skeleton", className)} style={style} aria-hidden="true" />;
}

/* --------------------------------------------------------------- Spinner */

export function Spinner({ label = "Loading", className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={className}>
      <span className="ui-spinner" aria-hidden="true" />
      <span className="ui-sr-only">{label}</span>
    </span>
  );
}

/* ---------------------------------------------------------------- States */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Every list renders loading, empty, error and success: this is the empty one. */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx("ui-state", className)}>
      {icon && <span className="ui-state__icon">{icon}</span>}
      <div>
        <p className="ui-state__title">{title}</p>
        {description && <p className="ui-state__description">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({ title = "That did not load", description, onRetry, className }: ErrorStateProps) {
  return (
    <div className={cx("ui-state", className)} role="alert">
      <span className="ui-state__icon ui-state__icon--danger">
        <WarningIcon />
      </span>
      <div>
        <p className="ui-state__title">{title}</p>
        {description && <p className="ui-state__description">{description}</p>}
      </div>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
      <path d="M236.8 188.1 148.9 36.6a24 24 0 0 0-41.8 0L19.2 188.1A24 24 0 0 0 40 224h176a24 24 0 0 0 20.8-35.9ZM128 80a8 8 0 0 1 8 8v56a8 8 0 0 1-16 0V88a8 8 0 0 1 8-8Zm0 112a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ Misc */

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cx("ui-kbd", className)}>{children}</kbd>;
}

export function Separator({ className }: { className?: string }) {
  return <div role="separator" className={cx("ui-separator", className)} />;
}
